import { useState, useEffect, useId } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Loader2,
  X,
  Plus,
  Save,
  Download,
  RotateCcw,
  Trash2,
  Copy,
  Check,
  Star,
  ExternalLink,
} from "lucide-react";
import { api, API_BASE, type ApiResponse } from "@/api/client";
import type {
  AdminMediaResponse,
  MediaResponse,
  MediaUsage,
  MediaUsageKind,
  MediaUsageResponse,
} from "@ckgo2/shared/types";
import type { FocalLike, FocalSource } from "@ckgo2/shared";
import { useMedia } from "@/hooks/useMedia";
import { mediaDetailHref } from "@/hooks/useMediaLibrary";
import { MEDIA_SOURCES_KEY, useMediaSources } from "@/hooks/useMediaSources";
import { blurStyle } from "@/lib/mediaStyle";
import { formatFileSize } from "@/lib/mediaFormat";
import { formatDate } from "@/lib/format";
import { getUserMessage } from "@/lib/errorMessages";
import DestinationPicker from "@/components/common/DestinationPicker";
import FocalPointEditor from "@/components/media/FocalPointEditor";
import FocalSurfacePreviews from "@/components/media/FocalSurfacePreviews";
import { type AlbumFolder } from "@/components/media/AlbumTree";
import {
  dragFocal,
  drawerFormFrom,
  drawerPatchBody,
  shouldSyncDrawerForm,
  usageLinkTarget,
  type DrawerForm,
} from "./MediaDetailDrawer.model";
import { withMediaTag } from "./mediaTags";

/** Right-side drawer to edit a single media row + its usage, albums, duplicates. */
export default function MediaDetailDrawer({
  id,
  inTrash,
  onClose,
  onChanged,
}: {
  id: string;
  inTrash: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isFetching } = useMedia(id);
  const media = data?.data;
  const hasMedia = !!media && media.id === id;

  // Formulář (`DrawerForm`) je JEDEN stav i s ID fotky, pro kterou platí. Bod ohniska nese
  // i režim a AI bod: tažení (`dragFocal`) ho přepne na ruční a AI bod drží, uložení pošle
  // `focalSource` jen při změně ručně / neručně (`drawerPatchBody`, nikdy `ai`).
  const [form, setForm] = useState<{ id: string; values: DrawerForm } | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const update = (patch: Partial<DrawerForm> | ((v: DrawerForm) => Partial<DrawerForm>)) =>
    setForm((f) =>
      f
        ? {
            ...f,
            values: { ...f.values, ...(typeof patch === "function" ? patch(f.values) : patch) },
          }
        : f,
    );

  // Formulář se plní JEDNOU pro dané `id`, a to až z čerstvých dat: refetch (★ z prohlížeče,
  // uložení) by jinak přepsal neuložený alt, tagy i nová pole, a stará keš (první render
  // s `isFetching`) by do formuláře dala hodnoty, které Uložit pak zapíše. Dokud formulář
  // neplatí pro `id`, zásuvka ukazuje načítání a patička nemá Uložit — při přechodu na jinou
  // fotku v připojené zásuvce (odkaz na živé dvojče) by jinak odešly hodnoty minulé fotky.
  const formId = form?.id ?? null;
  useEffect(() => {
    if (!media || !shouldSyncDrawerForm({ id, formId, hasMedia, isFetching })) return;
    setForm({ id, values: drawerFormFrom(media) });
    setSaveError(null);
  }, [media, hasMedia, isFetching, id, formId]);
  const values = hasMedia && form?.id === id ? form.values : null;

  const { data: usageData } = useQuery({
    queryKey: ["media-usage", id],
    queryFn: () => api.get<ApiResponse<MediaUsageResponse>>(`/api/admin/upload/${id}/usage`),
  });
  const usages = usageData?.data?.usages ?? [];

  const { data: sourceOptions = [] } = useMediaSources();

  const { data: dupData } = useQuery({
    queryKey: ["media-duplicates", id],
    queryFn: () =>
      api.get<ApiResponse<{ similar: MediaResponse[] }>>(`/api/admin/upload/${id}/duplicates`),
  });
  const duplicates = dupData?.data?.similar ?? [];

  // Album membership for this image (the "In albums" section).
  const { data: memberData } = useQuery({
    queryKey: ["media-album-membership", id],
    queryFn: () =>
      api.get<ApiResponse<{ id: string; name: string }[]>>(
        `/api/admin/media-folders/for-media/${id}`,
      ),
  });
  const memberAlbums = memberData?.data ?? [];
  const { data: allAlbumsData } = useQuery({
    queryKey: ["media-folders"],
    queryFn: () => api.get<ApiResponse<AlbumFolder[]>>("/api/admin/media-folders"),
  });
  const allAlbums = allAlbumsData?.data ?? [];
  const refreshAlbums = () => {
    queryClient.invalidateQueries({ queryKey: ["media-album-membership", id] });
    queryClient.invalidateQueries({ queryKey: ["media-folders"] });
  };
  const addAlbumMutation = useMutation({
    mutationFn: (folderId: string) =>
      api.post(`/api/admin/media-folders/${folderId}/items`, { mediaIds: [id] }),
    onSuccess: refreshAlbums,
  });
  const removeAlbumMutation = useMutation({
    mutationFn: (folderId: string) =>
      api.delete(`/api/admin/media-folders/${folderId}/items`, { mediaIds: [id] }),
    onSuccess: refreshAlbums,
  });

  // Bod po uložení bere z odpovědi, ne až z refetche: když se řádek nezmění (automatické
  // médium, se kterým se jen neuloženě hýbalo, a pak „Zpět na automatický“), drží query
  // strukturálním sdílením tutéž referenci `media` a synchronizace formuláře se nespustí.
  function onMediaSaved(res: ApiResponse<AdminMediaResponse>) {
    const saved = res.data;
    if (saved) {
      const { focal } = drawerFormFrom(saved);
      setForm((f) => (f && f.id === saved.id ? { ...f, values: { ...f.values, focal } } : f));
    }
    queryClient.invalidateQueries({ queryKey: ["media", id] });
    onChanged();
  }

  // Hodnoty formuláře i uložená fotka jdou do mutace jako proměnné (jeden zdroj vstupu).
  const saveMutation = useMutation({
    mutationFn: (input: { values: DrawerForm; current: AdminMediaResponse }) =>
      api.patch<ApiResponse<AdminMediaResponse>>(
        `/api/admin/upload/${input.current.id}`,
        drawerPatchBody(input.values, input.current),
      ),
    onMutate: () => setSaveError(null),
    onSuccess: (res) => {
      onMediaSaved(res);
      queryClient.invalidateQueries({ queryKey: MEDIA_SOURCES_KEY });
    },
    // Chyba uložení (např. 400 `media_country_not_country_level`) zůstane u formuláře;
    // vlastní `onError` záměrně nahrazuje výchozí toast z `main.tsx`.
    onError: (err: Error) => setSaveError(getUserMessage(err)),
  });

  const resetFocalMutation = useMutation({
    mutationFn: () =>
      api.patch<ApiResponse<AdminMediaResponse>>(`/api/admin/upload/${id}`, {
        focalSource: "auto",
      }),
    onSuccess: onMediaSaved,
  });

  const deleteMutation = useMutation({
    mutationFn: (force: boolean) =>
      api.delete(`/api/admin/upload/${id}${force ? "?force=true" : ""}`),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => api.post(`/api/admin/upload/${id}/restore`, {}),
    onSuccess: () => {
      onChanged();
      queryClient.invalidateQueries({ queryKey: ["media", id] });
    },
  });

  const addTag = () => {
    update((v) => {
      const tags = withMediaTag(v.tags, tagInput);
      return tags ? { tags } : {};
    });
    setTagInput("");
  };

  const altRequiredError = !values || values.alt.trim().length === 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-2xl dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
        data-media-drawer=""
        data-media-id={id}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("media.drawer.title")}
          </h2>
          <button
            onClick={onClose}
            aria-label={t("common.actions.close")}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!values || !media ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="flex-1 space-y-5 p-4">
            {/* Preview with LQIP */}
            <div className="overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
              <img
                src={media.url}
                alt={media.alt}
                className="max-h-64 w-full object-contain"
                style={blurStyle(media.blurDataUrl)}
              />
            </div>

            {/* Alt + title */}
            <div>
              <label className="label">{t("media.drawer.altLabel")}</label>
              <input
                value={values.alt}
                maxLength={125}
                onChange={(e) => update({ alt: e.target.value })}
                className={`input ${altRequiredError ? "input-error" : ""}`}
                placeholder={t("media.drawer.altPlaceholder")}
              />
              {altRequiredError && (
                <p className="text-error-600 mt-1 text-xs">{t("media.drawer.altRequired")}</p>
              )}
            </div>
            <div>
              <label className="label">{t("common.fields.title")}</label>
              <input
                value={values.title}
                onChange={(e) => update({ title: e.target.value })}
                className="input"
                placeholder={t("media.drawer.titlePlaceholder")}
              />
            </div>

            {/* Description (longer, for keyword search) */}
            <div>
              <label className="label">{t("common.fields.description")}</label>
              <textarea
                value={values.description}
                onChange={(e) => update({ description: e.target.value })}
                rows={2}
                maxLength={500}
                className="input"
                placeholder={t("media.drawer.descriptionPlaceholder")}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="label">{t("media.drawer.tags")}</label>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {values.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                  >
                    {tag}
                    <button
                      title={t("common.actions.remove")}
                      type="button"
                      onClick={() => update((v) => ({ tags: v.tags.filter((x) => x !== tag) }))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  className="input flex-1"
                  placeholder={t("media.drawer.tagPlaceholder")}
                />
                <button type="button" onClick={addTag} className="btn-ghost text-sm">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Countries, source, good photo */}
            <MediaClassificationFields
              countryIds={values.countryIds}
              onCountryIdsChange={(countryIds) => update({ countryIds })}
              source={values.source}
              sourceOptions={sourceOptions}
              onSourceChange={(source) => update({ source })}
              isStarred={values.isStarred}
              onStarredChange={(isStarred) => update({ isStarred })}
              disabled={inTrash}
            />

            {/* Focal point: stav, Zpět na automatický, editor bodu, náhledy ploch */}
            <MediaFocalField
              src={media.url}
              alt={media.alt}
              focal={values.focal}
              onFocalChange={(next) => update((v) => ({ focal: dragFocal(v.focal, next) }))}
              onReset={() => resetFocalMutation.mutate()}
              resetPending={resetFocalMutation.isPending}
              disabled={inTrash}
            />

            {/* Public URL + copy */}
            <div>
              <label className="label">{t("media.drawer.publicUrl")}</label>
              <div className="flex gap-2">
                <input readOnly value={media.url} className="input flex-1 text-xs" />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(media.url).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    });
                  }}
                  className="btn-ghost text-sm"
                  aria-label={t("media.drawer.copyUrl")}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Metadata */}
            <dl className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div>
                <dt className="font-medium">{t("media.drawer.dimensions")}</dt>
                <dd>
                  {media.width}×{media.height}
                </dd>
              </div>
              <div>
                <dt className="font-medium">{t("media.drawer.size")}</dt>
                <dd>{formatFileSize(media.sizeBytes)}</dd>
              </div>
              <div>
                <dt className="font-medium">{t("media.drawer.format")}</dt>
                <dd>{media.mimeType}</dd>
              </div>
              <div>
                <dt className="font-medium">{t("media.drawer.uploaded")}</dt>
                <dd>{formatDate(media.uploadedAt)}</dd>
              </div>
              {media.uploadedBy && (
                <div className="col-span-2">
                  <dt className="font-medium">{t("media.drawer.uploadedBy")}</dt>
                  <dd>{media.uploadedBy}</dd>
                </div>
              )}
            </dl>

            {/* Used in */}
            <div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                {t("media.drawer.usedIn")}
              </h3>
              <MediaUsageList usages={usages} onNavigate={onClose} />
            </div>

            {/* In albums */}
            <div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                {t("media.drawer.inAlbums")}
              </h3>
              <div className="mb-2 flex flex-wrap gap-1.5">
                {memberAlbums.length === 0 ? (
                  <p className="text-xs text-gray-500">{t("media.drawer.notInAlbum")}</p>
                ) : (
                  memberAlbums.map((a) => (
                    <span
                      key={a.id}
                      className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                    >
                      {a.name}
                      <button
                        type="button"
                        onClick={() => removeAlbumMutation.mutate(a.id)}
                        aria-label={t("media.drawer.removeFromAlbum", { name: a.name })}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))
                )}
              </div>
              <select
                value=""
                onChange={(e) => e.target.value && addAlbumMutation.mutate(e.target.value)}
                className="input text-sm"
              >
                <option value="">{t("media.albums.addToAlbumMenu")}</option>
                {allAlbums
                  .filter((a) => !memberAlbums.some((m) => m.id === a.id))
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Exact duplicates */}
            {duplicates.length > 0 && (
              <div>
                <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                  {t("media.drawer.exactDuplicates")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {duplicates.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      title={d.alt}
                      className="h-14 w-14 overflow-hidden rounded border border-gray-200 dark:border-gray-700"
                    >
                      <img src={d.thumbUrl} alt={d.alt} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {saveError && (
          <p
            data-media-drawer-error=""
            role="alert"
            className="text-error-600 border-t border-gray-200 px-4 py-2 text-sm dark:border-gray-700"
          >
            {saveError}
          </p>
        )}

        {/* Actions footer */}
        {media && (
          <div className="flex flex-wrap gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
            {!inTrash && values && (
              <button
                onClick={() => saveMutation.mutate({ values, current: media })}
                disabled={saveMutation.isPending || altRequiredError}
                className="btn-primary text-sm"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saveMutation.isSuccess ? t("common.states.saved") : t("common.actions.save")}
              </button>
            )}
            {media.r2KeyOriginal && (
              <a
                href={`${API_BASE}/api/media/${media.r2KeyOriginal}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost text-sm"
              >
                <Download className="h-4 w-4" /> {t("media.drawer.original")}
              </a>
            )}
            {inTrash ? (
              <MediaDrawerTrashAction
                liveTwinId={media.liveTwinId ?? null}
                restoring={restoreMutation.isPending}
                onRestore={() => restoreMutation.mutate()}
              />
            ) : (
              <button
                onClick={() => {
                  if (confirm(t("media.drawer.confirmTrash"))) deleteMutation.mutate(false);
                }}
                className="btn-ghost text-error-600 hover:bg-error-50 text-sm"
              >
                <Trash2 className="h-4 w-4" /> {t("common.actions.delete")}
              </button>
            )}
            <button
              onClick={() => {
                if (confirm(t("media.drawer.confirmHardDelete"))) deleteMutation.mutate(true);
              }}
              className="btn-ghost text-error-600 hover:bg-error-50 ml-auto text-sm"
            >
              <Trash2 className="h-4 w-4" /> {t("media.actions.hardDelete")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * „Používá se v“: všechna místa použití z `GET /:id/usage` (`usages`), jeden řádek na
 * místo (dvě pole téže entity = dva řádky), odkaz do editoru podle druhu (`USAGE_LINKS`).
 */
export function MediaUsageList({
  usages,
  onNavigate,
}: {
  usages: readonly MediaUsage[];
  /** Klik na odkaz (zásuvka se zavře). */
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const kindLabels: Record<MediaUsageKind, string> = {
    destination: t("mediaViewer.usageKind.destination"),
    trip: t("mediaViewer.usageKind.trip"),
    inspiration: t("mediaViewer.usageKind.inspiration"),
    blogPost: t("mediaViewer.usageKind.blogPost"),
    contentPage: t("mediaViewer.usageKind.contentPage"),
    guide: t("mediaViewer.usageKind.guide"),
    destinationTeam: t("mediaViewer.usageKind.destinationTeam"),
    premiumUpgrade: t("mediaViewer.usageKind.premiumUpgrade"),
    review: t("mediaViewer.usageKind.review"),
    departurePhoto: t("mediaViewer.usageKind.departurePhoto"),
    jobOffer: t("mediaViewer.usageKind.jobOffer"),
    newsletterCampaign: t("mediaViewer.usageKind.newsletterCampaign"),
    setting: t("mediaViewer.usageKind.setting"),
    user: t("mediaViewer.usageKind.user"),
  };
  if (usages.length === 0) {
    return <p className="text-xs text-gray-500">{t("media.drawer.notReferenced")}</p>;
  }
  return (
    <ul data-media-usage="" className="space-y-1 text-sm">
      {usages.map((u) => {
        const to = usageLinkTarget(u);
        const kind =
          u.kind === "trip" && u.isHotel ? t("mediaViewer.usageKind.hotel") : kindLabels[u.kind];
        return (
          <li
            key={`${u.kind}:${u.entityId}:${u.field}:${u.via}`}
            data-kind={u.kind}
            data-field={u.field}
            className="flex flex-wrap items-baseline gap-1.5"
          >
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {kind}
            </span>
            {to ? (
              <Link to={to} className="text-primary-600 hover:underline" onClick={onNavigate}>
                {u.label}
              </Link>
            ) : (
              <span className="text-gray-800 dark:text-gray-200">{u.label}</span>
            )}
            <span className="font-mono text-xs text-gray-400">{u.field}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Pole země, zdroj a ★ „Dobrá fotka“ ve formuláři detailu (ukládají se tlačítkem Uložit). */
export function MediaClassificationFields({
  countryIds,
  onCountryIdsChange,
  source,
  sourceOptions,
  onSourceChange,
  isStarred,
  onStarredChange,
  disabled,
}: {
  /** Země fotky (výběr více hodnot, jen zemská úroveň). */
  countryIds: string[];
  onCountryIdsChange: (value: string[]) => void;
  source: string;
  /** Dřívější zdroje z `useMediaSources()`, v pořadí API. */
  sourceOptions: readonly string[];
  onSourceChange: (value: string) => void;
  isStarred: boolean;
  onStarredChange: (value: boolean) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const countriesId = `${uid}-countries`;
  const sourceId = `${uid}-source`;
  const datalistId = `${uid}-sources`;
  return (
    <>
      <div data-media-countries="">
        <label className="label" htmlFor={countriesId}>
          {t("mediaViewer.drawer.countries")}
        </label>
        <DestinationPicker
          id={countriesId}
          mode="multiple"
          level="country"
          value={countryIds}
          onChange={onCountryIdsChange}
          disabled={disabled}
        />
      </div>
      <div>
        <label className="label" htmlFor={sourceId}>
          {t("mediaViewer.drawer.source")}
        </label>
        <input
          id={sourceId}
          data-media-source=""
          list={datalistId}
          maxLength={120}
          value={source}
          onChange={(e) => onSourceChange(e.target.value)}
          disabled={disabled}
          className="input"
          placeholder={t("mediaViewer.drawer.sourcePlaceholder")}
        />
        <datalist id={datalistId}>
          {sourceOptions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
      <div>
        <button
          type="button"
          data-media-star=""
          aria-pressed={isStarred}
          disabled={disabled}
          onClick={() => onStarredChange(!isStarred)}
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${
            isStarred
              ? "border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
              : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300"
          }`}
        >
          <Star className={`h-4 w-4 ${isStarred ? "fill-amber-400 text-amber-500" : ""}`} />
          {t("mediaViewer.drawer.starred")}
        </button>
      </div>
    </>
  );
}

/**
 * Sekce ohniska v detailu fotky: stav ohniska formuláře (`data-source` = `auto` | `ai` |
 * `manual`, po tažení `manual` už před uložením), „Zpět na automatický“ (jen u ručního bodu;
 * server vrátí AI bod, když ho fotka má), editor bodu a náhledy ploch webu, které jdou za bodem
 * formuláře už při tažení.
 */
export function MediaFocalField({
  src,
  alt,
  focal,
  onFocalChange,
  onReset,
  resetPending,
  disabled = false,
}: {
  src: string;
  alt: string;
  focal: FocalLike;
  onFocalChange: (next: { focalPointX: number; focalPointY: number }) => void;
  onReset: () => void;
  resetPending: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const stateLabel: Record<FocalSource, string> = {
    auto: t("media.focalState.auto"),
    ai: t("media.focalState.ai"),
    manual: t("media.focalState.manual"),
  };
  const manual = focal.focalSource === "manual";
  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <label className="label mb-0">{t("media.drawer.focalPoint")}</label>
          <div className="flex items-center gap-2">
            <span
              data-media-focal-state=""
              data-source={focal.focalSource}
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                manual
                  ? "bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
              }`}
            >
              {stateLabel[focal.focalSource]}
            </span>
            <button
              type="button"
              data-media-focal-reset=""
              className="btn-secondary px-2 py-1 text-xs"
              disabled={!manual || resetPending || disabled}
              onClick={onReset}
            >
              {t("media.focal.resetAuto")}
            </button>
          </div>
        </div>
        <FocalPointEditor
          src={src}
          alt={alt}
          focalPointX={focal.focalPointX}
          focalPointY={focal.focalPointY}
          onChange={onFocalChange}
        />
      </div>
      <FocalSurfacePreviews src={src} focal={focal} t={t} />
    </div>
  );
}

/**
 * Akce detailu fotky v koši: u kopie, jejíž obsah má živá fotka, odkaz na živou fotku místo
 * „Obnovit“ (obnovení by API odmítlo 409 `media_restore_live_twin`).
 */
export function MediaDrawerTrashAction({
  liveTwinId,
  restoring,
  onRestore,
}: {
  liveTwinId: string | null;
  restoring: boolean;
  onRestore: () => void;
}) {
  const { t } = useTranslation();
  if (liveTwinId) {
    return (
      <Link to={mediaDetailHref(liveTwinId)} data-media-live-twin="" className="btn-ghost text-sm">
        <ExternalLink className="h-4 w-4" /> {t("mediaViewer.drawer.liveTwin")}
      </Link>
    );
  }
  return (
    <button
      type="button"
      data-media-restore=""
      onClick={onRestore}
      disabled={restoring}
      className="btn-ghost text-sm"
    >
      <RotateCcw className="h-4 w-4" /> {t("media.actions.restore")}
    </button>
  );
}
